import CloseIcon from '@mui/icons-material/Close'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import ContainerSettlementList from 'components/inventory/containers/configured-service/deactivation/ContainerSettlementList'
import { useContainerSettlement } from 'components/inventory/containers/configured-service/deactivation/useContainerSettlement'
import T from 'T'
import { Z_INDEX } from 'theme/zIndex'

import type { SettlementYard } from 'components/inventory/containers/configured-service/deactivation/ContainerSettlementList'
import type { RetrievalResolution, SettleableRow } from 'components/inventory/containers/configured-service/deactivation/settlement'

export interface DeactivateAccountResult {
  resolveLater: boolean
  resolutions: Record<string, RetrievalResolution>
}

// Grouped by the service they sit on, deactivated services included — nothing has ever asked.
export type AccountSettleableRow = SettleableRow & {
  containerName?: string | null
  serialNumber?: string | null
  groupLabel?: string | null
  groupCaption?: string | null
  groupKey?: string | null
}

interface DeactivateAccountDrawerProps {
  isOpen: boolean
  accountName?: string
  description: string
  rows: AccountSettleableRow[]
  yards: SettlementYard[]
  isFetchingRows?: boolean
  // The rows could not be read. Said out loud rather than shown as an account with nothing on it.
  hasRowError?: boolean
  isLoading?: boolean
  defaultYardId?: string
  onClose: () => void
  onConfirm: (result: DeactivateAccountResult) => void
}

// The outcomes travel with the request for the run to apply; an account ends now, so no timing ask.
const DeactivateAccountDrawer = ({
  isOpen,
  accountName = '',
  description,
  rows,
  yards,
  isFetchingRows = false,
  hasRowError = false,
  isLoading = false,
  defaultYardId,
  onClose,
  onConfirm,
}: DeactivateAccountDrawerProps) => {
  const [resolveLater, setResolveLater] = useState(false)
  const today = useMemo(() => new Date(), [])

  const settlement = useContainerSettlement(rows, { defaultYardId: defaultYardId ?? yards[0]?.id, defaultDate: today })
  const { reset, settled, real } = settlement

  useEffect(() => {
    if (isOpen) return
    setResolveLater(false)
    reset()
  }, [isOpen, reset])

  const handleResolveLaterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setResolveLater(event.target.checked), [])

  const handleConfirm = useCallback(
    () => onConfirm({ resolveLater, resolutions: resolveLater ? {} : settlement.resolutions }),
    [onConfirm, resolveLater, settlement.resolutions]
  )

  // Nothing to answer is not a reason to block; an account of customer-owned cans confirms straight away.
  const canConfirm = !isFetchingRows && (resolveLater || real.length === 0 || settled)

  // Above the app navigation, or the fixed top bar covers the drawer's own header.
  return (
    <Drawer anchor="right" open={isOpen} sx={{ zIndex: Z_INDEX.ELEVATED_DIALOG }} onClose={onClose}>
      <Stack sx={{ height: '100%', width: { xs: '100vw', md: 920 } }}>
        <Stack sx={{ px: 3, py: 2 }}>
          <Stack alignItems="center" direction="row" justifyContent="space-between">
            <Typography variant="h6">{T.DEACTIVATE_ACCOUNT_DIALOG}</Typography>
            <IconButton aria-label={T.CLOSE} size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          {accountName && (
            <Typography color="text.secondary" variant="body2">
              {accountName}
            </Typography>
          )}
        </Stack>
        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          <Stack spacing={2.5}>
            <Typography variant="body2">{description}</Typography>

            {hasRowError && <Alert severity="warning">{T.ACCOUNT_CONTAINERS_COULD_NOT_LOAD}</Alert>}

            {isFetchingRows && <Skeleton height={180} variant="rounded" />}

            {!isFetchingRows && rows.length > 0 && (
              <>
                <Divider />
                <ContainerSettlementList disabled={resolveLater} settlement={settlement} yards={yards} />
              </>
            )}
          </Stack>
        </Box>

        <Divider />
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1} sx={{ px: 3, py: 2 }}>
          {/* In the footer, not under the list: on a large account the list is hundreds of rows. */}
          {real.length > 0 ? (
            <FormControlLabel
              control={<Checkbox checked={resolveLater} size="small" onChange={handleResolveLaterChange} />}
              label={<Typography variant="body2">{T.RESOLVE_CONTAINERS_LATER}</Typography>}
            />
          ) : (
            <Box />
          )}
          <Stack direction="row" spacing={1}>
            <Button color="inherit" onClick={onClose}>
              {T.DISCARD}
            </Button>
            {/* Amber, not red: deactivation is reversible, and red puts it in the same class as a delete. */}
            <Button color="warning" disabled={!canConfirm} loading={isLoading} variant="contained" onClick={handleConfirm}>
              {T.DEACTIVATE_ACCOUNT_DIALOG}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default DeactivateAccountDrawer
